const post = {
  slug: 'youtube-title-and-thumbnail',
  cluster: 'topics',
  eyebrow: 'Packaging',
  title: 'Title and thumbnail are one unit, not two',
  description:
    'The most common packaging mistake is having both say the same thing. Here is what each should carry, and how to test the pair.',
  keywords: [
    'youtube thumbnail', 'youtube title', 'youtube packaging', 'click through rate',
    'thumbnail design', 'youtube title tips', 'ctr youtube',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-title-and-thumbnail.webp',
    alt: 'A thumbnail and title shown together, with an arrow marking the information each supplies that the other does not.',
  },
  lede:
    'Most packaging advice treats title and thumbnail separately. That is the mistake — they are read together, in about a second, and the job is to make one hook out of two halves.',
  sections: [
    {
      heading: 'The redundancy problem',
      body: [
        {
          type: 'para',
          text: 'A thumbnail showing a bridge collapsing, next to the title "The bridge that collapsed". The pair has communicated one fact using two slots.',
        },
        {
          type: 'para',
          text: 'You have very little space to make someone curious. Spending half of it repeating the other half is the most common and most expensive packaging error.',
        },
        {
          type: 'example',
          badLabel: 'Redundant',
          goodLabel: 'Complementary',
          bad: 'Image: a collapsing bridge. Title: "The bridge that collapsed".',
          good: 'Image: a collapsing bridge. Title: "It passed every inspection".',
          note: 'The second pair creates a question the viewer needs the video to resolve. Neither half does that alone.',
        },
      ],
    },
    {
      heading: 'The division of labour',
      body: [
        {
          type: 'para',
          text: 'A workable default:',
        },
        {
          type: 'list',
          items: [
            '**The thumbnail supplies the subject.** What this is about, readable instantly. Concrete objects, real photographs, one clear focal point.',
            '**The title supplies the tension.** The thing that makes the subject a question rather than a topic.',
          ],
        },
        {
          type: 'para',
          text: 'It works in reverse too — a title naming the subject plainly beside an image that is visually confusing in a way the video explains. What must not happen is both halves doing the same job.',
        },
      ],
    },
    {
      heading: 'Thumbnails: the constraints that matter',
      body: [
        {
          type: 'para',
          text: 'Most thumbnails are designed at full size and consumed at the size of a postage stamp on a phone. Design for that.',
        },
        {
          type: 'list',
          items: [
            '**One focal point.** Two competing subjects at small size read as neither.',
            '**Three or four words maximum**, if any. Heavy, condensed, high contrast. A sentence is invisible.',
            '**Text must not repeat the title.** It is the same redundancy trap, and it is the most common version of it.',
            '**Real images beat rendered ones** for anything about real subjects. A photograph of the actual person or object is more credible and more distinctive than an illustration of the idea.',
            '**Test it small.** Shrink to roughly 120 pixels wide and look. If you cannot tell what it is, neither can anyone else.',
          ],
        },
        {
          type: 'para',
          text: 'If the file is large, [compressing it](/free-tools/compress/image) before upload is worth doing — it happens in your browser and nothing is uploaded anywhere.',
        },
      ],
    },
    {
      heading: 'Titles: name the specific thing',
      body: [
        {
          type: 'para',
          text: 'The strongest titles contain something concrete: a name, a number, a date, a claim. Vague titles fail twice — they are less compelling *and* they tell search nothing about what the video covers.',
        },
        {
          type: 'example',
          bad: 'The Truth About Airline Pricing',
          good: 'Why the same seat costs four different prices on one flight',
          note: 'The second names the actual phenomenon, which is both more interesting and more findable.',
        },
        {
          type: 'para',
          text: 'Avoid the constructions that have been used into meaninglessness — "the truth about", "what they do not want you to know", "you will not believe". They signal low information density, and audiences have learned to read them that way.',
        },
      ],
    },
    {
      heading: 'The promise you have to keep',
      body: [
        {
          type: 'para',
          text: 'Packaging sets an expectation the first thirty seconds either meets or breaks. Overpromise and you get the worst possible combination: high click-through and a retention cliff, which teaches the system the video does not satisfy the people it attracts.',
        },
        {
          type: 'para',
          text: 'A useful discipline is to write the title before the script and then hold the script to it. If the video does not deliver what the title claims, one of the two is wrong — and it is usually more honest to change the title.',
        },
        {
          type: 'callout',
          title: 'Do not put a figure in a title you cannot defend',
          text: 'A specific number is the most clickable thing you can put in a title, which is exactly why the temptation to invent one is strong. A wrong figure in a title is the most visible possible place to be wrong.',
        },
      ],
    },
    {
      heading: 'Iterate on it',
      body: [
        {
          type: 'para',
          text: 'Packaging is the cheapest thing to change after publishing and the only one you can change without touching the video. A video with good retention and poor click-through is the ideal candidate: you already know people who watch it stay.',
        },
        {
          type: 'para',
          text: 'See [diagnosing a channel](/blogs/youtube-channel-not-growing) for reading the two numbers together.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Do faceless channels do worse on thumbnails without a face in them?',
      a: 'Faces attract attention, which is a real effect, but it is not the only thing that does — a clear object, a striking image or a legible contrast works too. What consistently underperforms is an abstract or generic image, with or without a face.',
    },
    {
      q: 'Should I A/B test thumbnails?',
      a: 'Where the platform offers it, yes — it is more reliable than swapping one for another and comparing across different time periods, since traffic conditions change underneath you.',
    },
  ],
  related: ['youtube-hook-writing', 'youtube-channel-not-growing', 'youtube-topic-research'],
};

export default post;
